import React, { useState } from 'react';
import { MapPin, Globe, RefreshCw, CheckCircle2, AlertTriangle, ArrowRight, ShieldCheck, Compass } from 'lucide-react';
import Button from '../../shared/Button';
import ModalShell from '../../shared/ModalShell';
import useVendorLocationAlert from '../../../hooks/useVendorLocationAlert';

const VendorLocationManagerCard = ({ onNavigateToBusinessTab }) => {
 const {
 isMismatch,
 savedLocation,
 detectedLocation,
 isDetecting,
 isUpdating,
 updateToDetectedLocation,
 refreshDetection,
 } = useVendorLocationAlert();

 const [confirmUpdateModal, setConfirmUpdateModal] = useState(false);

 const handleUpdate = async () => {
 setConfirmUpdateModal(false);
 await updateToDetectedLocation();
 };

 const savedDisplay = savedLocation.display || (savedLocation.country ? `${savedLocation.city ? savedLocation.city + ', ' : ''}${savedLocation.country}` : 'Not configured');
 const detectedDisplay = detectedLocation.display || (detectedLocation.country ? `${detectedLocation.city ? detectedLocation.city + ', ' : ''}${detectedLocation.country}` : 'Detecting...');

 return (
 <div className="vendor-card overflow-hidden mb-6">
 {/* Header */}
 <div className="p-4 sm:p-6 border-b border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-surface-muted/80">
 <div className="flex items-start sm:items-center gap-3">
 <div className="w-10 h-10 rounded-xl bg-gray-100 border border-border flex items-center justify-center text-ink flex-shrink-0">
 <Compass className="w-5 h-5" />
 </div>
 <div>
 <div className="flex items-center gap-2 flex-wrap">
 <h2 className="text-base sm:text-lg font-bold text-ink">
 Business Operating Location
 </h2>
 {isMismatch ? (
 <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
 <AlertTriangle className="w-3 h-3" />
 Different Location Detected
 </span>
 ) : (
 <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
 <CheckCircle2 className="w-3 h-3" />
 Location Synced
 </span>
 )}
 </div>
 <p className="text-xs sm:text-sm text-ink-tertiary mt-0.5">
 Your registered location determines where clients find you in search results and nearby service directories.
 </p>
 </div>
 </div>

 <div className="flex items-center gap-2 self-start sm:self-center">
 <Button
 type="button"
 variant="outline"
 size="sm"
 onClick={refreshDetection}
 disabled={isDetecting}
 className="text-xs flex items-center border-gray-300 text-ink hover:bg-surface-muted"
 title="Re-check current GPS and IP location"
 >
 <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${isDetecting ? 'animate-spin' : 'text-gray-500'}`} />
 {isDetecting ? 'Detecting...' : 'Detect Location'}
 </Button>
 </div>
 </div>

 <div className="p-4 sm:p-6 space-y-6">
 {/* Mismatch Alert Box */}
 {isMismatch && (
 <div className="rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50/90 to-orange-50/70 p-4 sm:p-5">
 <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
 <div className="flex items-start gap-3">
 <div className="p-2 rounded-lg bg-amber-100 text-amber-700 flex-shrink-0">
 <Globe className="w-5 h-5" />
 </div>
 <div>
 <h4 className="text-sm font-semibold text-amber-950">
 Would you like to update your business operating location?
 </h4>
 <p className="mt-1 text-xs sm:text-sm text-amber-800 leading-relaxed">
 You are currently connecting from <strong className="font-semibold text-amber-950">{detectedDisplay}</strong>, but your business operating location is saved as <strong className="font-semibold text-amber-950">{savedDisplay}</strong>. We do not change your location automatically.
 </p>
 </div>
 </div>

 <div className="flex flex-wrap items-center gap-2 sm:gap-3 flex-shrink-0">
 <Button
 type="button"
 size="sm"
 onClick={() => setConfirmUpdateModal(true)}
 disabled={isUpdating}
 className="bg-amber-600 hover:bg-amber-700 text-white shadow-sm text-xs sm:text-sm font-medium"
 >
 {isUpdating ? (
 <>
 <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
 Updating...
 </>
 ) : (
 <>
 <MapPin className="w-3.5 h-3.5 mr-1.5" />
 Update to {detectedLocation.city || detectedLocation.country}
 </>
 )}
 </Button>

 {onNavigateToBusinessTab && (
 <Button
 type="button"
 variant="outline"
 size="sm"
 onClick={onNavigateToBusinessTab}
 className="border-amber-300 text-amber-900 hover:bg-amber-100/50 text-xs sm:text-sm"
 >
 Custom Edit
 </Button>
 )}
 </div>
 </div>
 </div>
 )}

 {/* Location Details Grid */}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 {/* Registered Location Card */}
 <div className="rounded-xl border border-border bg-surface-muted p-4 sm:p-5 flex flex-col justify-between">
 <div>
 <div className="flex items-center justify-between mb-3">
 <div className="flex items-center gap-2">
 <div className="w-7 h-7 rounded-md bg-emerald-100 text-emerald-700 flex items-center justify-center">
 <MapPin className="w-4 h-4" />
 </div>
 <span className="text-xs font-bold uppercase tracking-wider text-gray-500">
 Active Registered Storefront
 </span>
 </div>
 <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-100 text-emerald-800">
 Client Facing
 </span>
 </div>

 <div className="space-y-2 text-sm">
 <div>
 <span className="text-xs text-gray-500 block">Location:</span>
 <p className="font-semibold text-gray-900 text-base">{savedDisplay}</p>
 </div>

 {savedLocation.address && (
 <div>
 <span className="text-xs text-gray-500 block">Address:</span>
 <p className="text-gray-700">{savedLocation.address}</p>
 </div>
 )}

 <div className="grid grid-cols-2 gap-2 pt-1">
 <div>
 <span className="text-xs text-gray-500 block">Country:</span>
 <p className="font-medium text-gray-800">{savedLocation.country || 'Not set'}</p>
 </div>
 <div>
 <span className="text-xs text-gray-500 block">City / State:</span>
 <p className="font-medium text-gray-800">{savedLocation.city ? `${savedLocation.city}${savedLocation.state ? ', ' + savedLocation.state : ''}` : 'Not set'}</p>
 </div>
 </div>

 {(savedLocation.latitude != null && savedLocation.longitude != null) && (
 <div className="pt-1 text-xs text-gray-500 font-mono">
 Coordinates: {parseFloat(savedLocation.latitude).toFixed(4)}, {parseFloat(savedLocation.longitude).toFixed(4)}
 </div>
 )}
 </div>
 </div>

 <div className="mt-4 pt-3 border-t border-gray-200/80 flex items-center justify-between">
 <span className="text-xs text-gray-500">Listed in search directory</span>
 {onNavigateToBusinessTab && (
 <button
 type="button"
 onClick={onNavigateToBusinessTab}
 className="text-xs font-medium text-ink hover:text-ink flex items-center gap-1 hover:underline"
 >
 Edit in Business Profile
 <ArrowRight className="w-3 h-3" />
 </button>
 )}
 </div>
 </div>

 {/* Detected Live Connection Card */}
 <div className={`rounded-xl border p-4 sm:p-5 flex flex-col justify-between ${ isMismatch ? 'border-amber-200 bg-amber-50/30' : 'border-border bg-surface-muted' }`}>
 <div>
 <div className="flex items-center justify-between mb-3">
 <div className="flex items-center gap-2">
 <div className={`w-7 h-7 rounded-md flex items-center justify-center ${ isMismatch ? 'bg-amber-100 text-amber-700' : 'bg-surface-muted text-ink' }`}>
 <Globe className="w-4 h-4" />
 </div>
 <span className="text-xs font-bold uppercase tracking-wider text-gray-500">
 Live Connection / IP Location
 </span>
 </div>
 <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold ${ isMismatch ? 'bg-amber-100 text-amber-800' : 'bg-surface-muted text-ink' }`}>
 {isDetecting ? 'Detecting...' : 'Current Session'}
 </span>
 </div>

 <div className="space-y-2 text-sm">
 <div>
 <span className="text-xs text-gray-500 block">Detected Region:</span>
 <p className="font-semibold text-gray-900 text-base">
 {detectedLocation.hasDetectedLocation ? detectedDisplay : (isDetecting ? 'Detecting current location...' : 'Detection unavailable')}
 </p>
 </div>

 <div className="grid grid-cols-2 gap-2 pt-1">
 <div>
 <span className="text-xs text-gray-500 block">Detected Country:</span>
 <p className="font-medium text-gray-800">{detectedLocation.country || (isDetecting ? 'Detecting...' : 'N/A')}</p>
 </div>
 <div>
 <span className="text-xs text-gray-500 block">Detected City / State:</span>
 <p className="font-medium text-gray-800">
 {detectedLocation.city ? `${detectedLocation.city}${detectedLocation.state ? ', ' + detectedLocation.state : ''}` : (isDetecting ? 'Detecting...' : 'N/A')}
 </p>
 </div>
 </div>

 {(detectedLocation.latitude != null && detectedLocation.longitude != null) && (
 <div className="pt-1 text-xs text-gray-500 font-mono">
 Coordinates: {parseFloat(detectedLocation.latitude).toFixed(4)}, {parseFloat(detectedLocation.longitude).toFixed(4)}
 </div>
 )}
 </div>
 </div>

 <div className="mt-4 pt-3 border-t border-gray-200/80 flex items-center justify-between">
 <span className="text-xs text-gray-500">
 {isMismatch ? 'Does not match storefront' : 'Matches storefront'}
 </span>
 {detectedLocation.hasDetectedLocation && (
 <button
 type="button"
 onClick={() => setConfirmUpdateModal(true)}
 disabled={isUpdating}
 className="text-xs font-medium text-ink hover:text-ink flex items-center gap-1 hover:underline"
 >
 Apply this location
 <ArrowRight className="w-3 h-3" />
 </button>
 )}
 </div>
 </div>
 </div>

 {/* Informative Security & Transparency Note */}
 <div className="flex items-start gap-2.5 p-3.5 bg-surface-muted/60 rounded-lg border border-border text-xs text-ink">
 <ShieldCheck className="w-4 h-4 text-ink flex-shrink-0 mt-0.5" />
 <div className="leading-relaxed">
 <span className="font-semibold text-ink">Automatic Location Protection: </span>
 Vendalist will <strong>never automatically change</strong> your registered business location when you travel abroad, move temporarily, or browse via VPN. IP/GPS is a hint only — marketplace Markets and your chosen operating country control where you appear and how tax/payouts work. You must explicitly apply a change here.
 </div>
 </div>
 </div>

 {/* Confirmation Modal */}
 <ModalShell
 isOpen={confirmUpdateModal}
 onClose={() => !isUpdating && setConfirmUpdateModal(false)}
 labelledBy="confirm-location-update-title"
 panelClassName="max-w-lg"
 >
 <div className="bg-surface rounded-lg shadow-sm border border-border overflow-hidden p-6 text-left">
 <div className="flex items-center gap-3 mb-4">
 <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
 <MapPin className="w-5 h-5" />
 </div>
 <div>
 <h3 id="confirm-location-update-title" className="text-lg font-bold text-ink">
 Update Business Location?
 </h3>
 <p className="text-xs text-ink-tertiary">
 Confirm change to your primary operating storefront
 </p>
 </div>
 </div>

 <div className="space-y-3 text-sm text-ink-secondary mb-6 bg-surface-muted p-4 rounded-xl border border-border">
 <p>
 You are about to change your business operating location:
 </p>
 <div className="flex items-center justify-between text-xs sm:text-sm py-1 border-b border-border">
 <span className="text-ink-tertiary">From:</span>
 <span className="font-semibold text-ink">{savedDisplay}</span>
 </div>
 <div className="flex items-center justify-between text-xs sm:text-sm py-1">
 <span className="text-ink-tertiary">To:</span>
 <span className="font-semibold text-amber-700">{detectedDisplay}</span>
 </div>
 <p className="text-xs text-ink-tertiary pt-2">
 Clients in {detectedLocation.city || detectedLocation.country} will now be able to discover your services in their local searches.
 </p>
 </div>

 <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
 <Button
 type="button"
 variant="outline"
 onClick={() => setConfirmUpdateModal(false)}
 disabled={isUpdating}
 className="rounded-md"
 >
 Cancel
 </Button>
 <Button
 type="button"
 onClick={handleUpdate}
 disabled={isUpdating}
 className="bg-ink hover:bg-black text-white rounded-md"
 >
 {isUpdating ? (
 <>
 <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
 Updating...
 </>
 ) : (
 'Confirm Location Change'
 )}
 </Button>
 </div>
 </div>
 </ModalShell>
 </div>
 );
};

export default VendorLocationManagerCard;
